import type { ActivityCheckResult, ContactsReviewResult } from './queries';

/**
 * **Ce qui a été relu, ce qui ne l'a pas été — assemblé à UN seul endroit du bureau.**
 *
 * Le 7 septembre 2026, deux écrans montaient cette paire chacun de son côté, et tous les deux
 * de la même façon fautive : ils prenaient la liste « pas relu » de la vérification automatique
 * (état « le jugement n'a pas eu lieu ») et lui ajoutaient la moitié « jugement » de la relecture.
 * Résultat à l'écran, deux lignes voisines :
 *
 * > **CE QUI A ÉTÉ RELU** — « Si deux fiches sont la même personne écrite de deux façons
 * > différentes. »
 * > **CE QUI N'A PAS ÉTÉ RELU** — « Si deux fiches qui se ressemblent sans être identiques sont
 * > bien la même personne. »
 *
 * Le serveur rend désormais, pour chacun des deux états, une paire **entière et déjà cohérente**
 * (`reviewScope`, `packages/core-domain/src/checks/review-scope.ts` : une seule liste, partitionnée
 * — une phrase ne peut pas tomber des deux côtés). Il ne reste donc ici qu'un CHOIX entre deux
 * paires, jamais une concaténation : c'est tout ce que cette fonction fait, et c'est pour cela
 * qu'elle existe plutôt que d'être recopiée dans chaque écran.
 *
 * Les deux appelants — `activity/ActivityBoard.tsx` (l'écran de la consigne) et
 * `host/reviewWork.ts` (la capacité `ctx.review()` ouverte aux apps, ADR-014) — passent par ici.
 * Un troisième écran qui afficherait ces listes devra en faire autant.
 */
export interface ReviewScopeLists {
  /** Ce qui a été relu, dans l'ordre du serveur. */
  looked: string[];
  /** Ce qui ne l'a pas été. **Disjoint de `looked`** : garanti par le serveur, pas reconstruit ici. */
  notLooked: string[];
}

/**
 * `judgment` vaut `null` quand la relecture par jugement n'a pas eu lieu du tout (jamais demandée),
 * et porte un `status: 'unavailable'` quand elle a été refusée (pas de clé, plafond atteint,
 * fournisseur en panne). Dans les deux cas, c'est la paire de la vérification automatique qui
 * s'applique : rien n'a été jugé.
 */
export function reviewScopeOf(
  check: Pick<ActivityCheckResult, 'verified' | 'notVerified'>,
  judgment: ContactsReviewResult | null,
): ReviewScopeLists {
  if (judgment === null || judgment.status !== 'ok') {
    return { looked: check.verified, notLooked: check.notVerified };
  }
  return { looked: judgment.verified, notLooked: judgment.notVerified };
}
